import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { subscribeUserNotifications, UnreadNotification } from '@/services/chatRealtimeService'
import { useAuth } from '@/contexts/AuthContext'

interface NotificationContextValue {
  unreadByUser: Record<string, number>
  totalUnread: number
  unreadDetails: Record<string, UnreadNotification>
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth()
  const [unreadByUser, setUnreadByUser] = useState<Record<string, number>>({})
  const [unreadDetails, setUnreadDetails] = useState<Record<string, UnreadNotification>>({})

  useEffect(() => {
    // ログアウト・アカウント切替時に前のユーザーの未読が残らないよう毎回クリアする
    setUnreadByUser({})
    setUnreadDetails({})

    const firebaseUid = user?.uid
    if (!firebaseUid) return

    const unsubscribe = subscribeUserNotifications(firebaseUid, (byUser, details) => {
      setUnreadByUser(byUser)
      setUnreadDetails(details)
    })
    return () => unsubscribe()
  }, [user?.uid])

  const totalUnread = Object.values(unreadByUser).reduce((sum, count) => sum + count, 0)

  return (
    <NotificationContext.Provider value={{ unreadByUser, totalUnread, unreadDetails }}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) throw new Error('useNotifications must be used within NotificationProvider')
  return context
}
